import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { ServerAuthUrlPipe } from 'auth';
import { I18nService, TranslationModule } from 'i18n';
import { switchMap } from 'rxjs';
import { CatalogCategory, CatalogCourse } from './program-catalog.models';
import { ProgramCatalogService } from './program-catalog.service';

/**
 * Program Catalog overview (Wolfpack Assets). Lists every real course grouped by
 * category, in the current UI language. Clicking a course:
 *   - free / already owned -> the course content viewer (chapters + PDFs)
 *   - not owned            -> the course detail + buy page
 * Entitlement is enforced server-side by the media framework; the flags here just
 * drive where the click goes.
 */
@Component({
  selector: 'app-program-catalog-page',
  standalone: true,
  imports: [CommonModule, TranslationModule, ServerAuthUrlPipe],
  templateUrl: './program-catalog-page.component.html',
  styleUrls: ['./program-catalog-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProgramCatalogPageComponent {
  private catalogService = inject(ProgramCatalogService);
  private i18n = inject(I18nService);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);

  categories = signal<CatalogCategory[]>([]);
  loading = signal(true);
  error = signal(false);

  constructor() {
    this.i18n.currentLanguage$
      .pipe(
        switchMap(lang => this.catalogService.getCatalog(lang)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: res => {
          this.categories.set(res.categories || []);
          this.loading.set(false);
        },
        error: () => {
          this.error.set(true);
          this.loading.set(false);
        }
      });
  }

  openCourse(course: CatalogCourse): void {
    const lang = this.i18n.getLanguage() || 'en';
    // Owned repurchasable courses (and not-yet-owned buyable ones) go to the detail
    // page, which offers both viewing and buying (again). Free / owned buy-once
    // courses open the content directly.
    const canBuyAgain = course.owned && course.repurchasable;
    // Bundles have no content of their own -> always the detail page. Regular
    // accessible courses (that aren't re-buyable) open their content directly.
    if (!course.is_bundle && course.accessible && !canBuyAgain) {
      this.router.navigate([`/${lang}/course`, course.course_ref]);
    } else {
      this.router.navigate([`/${lang}/program-catalog`, course.program_id]);
    }
  }
}
